import { useEffect, useMemo, useRef, useState } from "react";
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Loader2, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { priceDisplay } from "../lib/format";
import { useGeo } from "../lib/useGeo";
import { trackAddPaymentInfo } from "../lib/tiktok";
import { snapInitiatePayment } from "../lib/snapchat";
import { getStripe } from "../lib/stripe";
import { toast } from "sonner";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Tiny i18n helper — AI funnel renders English (USA market); everything else stays Arabic.
const T = {
  ar: {
    orPayCard: "أو ادفع بالبطاقة",
    payNow: "ادفع الآن",
    processing: "جاري معالجة الدفع...",
    loading: "جاري تحميل نموذج الدفع...",
    initError: "تعذّر تهيئة بوابة الدفع",
    paySuccess: "تم الدفع بنجاح",
    timeoutMsg: "حدث تأخير في معالجة الدفع. حاول مجدداً — لم يتم خصم أي مبلغ.",
    confirmFailPrefix: "تعذّر إتمام الدفع",
    confirmFailDefault: "تعذّر إتمام الدفع، تحقق من بيانات البطاقة وحاول مجدداً.",
    confirmFailCard: "تعذّر إتمام الدفع، تحقق من بيانات البطاقة.",
    expressFail: "تعذّر إتمام الدفع.",
    expressRetry: "تعذّر إتمام الدفع، حاول مجدداً.",
    stripeNotReady: "Stripe is not ready yet.",
    secureNote: "دفع مشفّر بواسطة Stripe · لا نحفظ بيانات البطاقة",
  },
  en: {
    orPayCard: "Or pay with card",
    payNow: "Pay Now",
    processing: "Processing payment...",
    loading: "Loading payment form...",
    initError: "Could not initialize the payment gateway",
    paySuccess: "Payment successful",
    timeoutMsg: "Payment is taking longer than expected. Please try again — you have not been charged.",
    confirmFailPrefix: "Payment could not be completed",
    confirmFailDefault: "Payment could not be completed. Please check your card details and try again.",
    confirmFailCard: "Payment could not be completed. Please check your card details.",
    expressFail: "Payment could not be completed.",
    expressRetry: "Payment could not be completed. Please try again.",
    stripeNotReady: "Stripe is not ready yet.",
    secureNote: "Encrypted payment by Stripe · We don't store your card details",
  },
};

function InnerForm({ orderId, amount, pkg, onSuccess, displayPriceOverride, themePink, themeAI }) {
  const stripe = useStripe();
  const elements = useElements();
  const country = useGeo();
  const t = themeAI ? T.en : T.ar;
  const dir = themeAI ? "ltr" : "rtl";
  // displayPriceOverride lets the parent (Checkout) force a specific
  // user-facing label (e.g. "699 جنيه" for the EG-via-Stripe flow) while
  // Stripe still charges the underlying USD amount.
  const price = displayPriceOverride || priceDisplay(amount, country);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [walletReady, setWalletReady] = useState(false);

  // Fire TikTok / Snap "AddPaymentInfo" once the form is interactive — this
  // signals checkout intent for ad attribution even without an email.
  const addInfoFiredRef = useRef(false);
  useEffect(() => {
    if (!addInfoFiredRef.current && stripe && elements) {
      trackAddPaymentInfo(pkg);
      snapInitiatePayment(pkg, "");
      addInfoFiredRef.current = true;
    }
  }, [stripe, elements, pkg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrorMsg("");
    const returnUrl = `${window.location.origin}/success/${orderId}`;

    // Use the visitor's actual ISO-2 country for billing.address.country so the
    // value matches their card-issuer country. Hardcoding "SA" caused Stripe
    // Radar / issuer to decline Egyptian / non-Gulf Visa cards. Fall back to
    // "SA" only when useGeo couldn't resolve a country (XX or empty).
    const billingCountry = country && country.length === 2 && country !== "XX" ? country : "SA";

    // Safety net: surface a retry message if confirmPayment hangs > 60s.
    let timedOut = false;
    const safetyTimer = setTimeout(() => {
      timedOut = true;
      setSubmitting(false);
      setErrorMsg(t.timeoutMsg);
    }, 60_000);

    let result;
    try {
      result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: returnUrl,
          // billing fields are hidden in the UI; provide the visitor's detected
          // country so Stripe Radar/AVS sees a value that matches the card's
          // issuer country (reduces foreign-card decline rate).
          payment_method_data: {
            billing_details: { address: { country: billingCountry } },
          },
        },
      });
    } catch (err) {
      clearTimeout(safetyTimer);
      if (timedOut) return;
      const detail = err?.message || err?.error?.message || "";
      console.error("Stripe confirmPayment threw:", err);
      setErrorMsg(detail ? `${t.confirmFailPrefix}: ${detail}` : t.confirmFailDefault);
      setSubmitting(false);
      return;
    }
    clearTimeout(safetyTimer);
    if (timedOut) return;

    const { error, paymentIntent } = result || {};
    if (error) {
      console.error("Stripe confirmPayment error:", error);
      setErrorMsg(error.message || error.code || t.confirmFailCard);
      setSubmitting(false);
      return;
    }
    if (paymentIntent?.status === "succeeded") {
      toast.success(t.paySuccess);
      onSuccess();
      return;
    }
    if (paymentIntent && (paymentIntent.status === "processing" || paymentIntent.status === "requires_action")) {
      return;
    }
    setSubmitting(false);
  };

  // === Express Checkout (Apple Pay / Google Pay) ===
  // IMPORTANT: Stripe's ExpressCheckoutElement contract REQUIRES this handler to
  // accept an `event` parameter and call `event.paymentFailed({ reason, message })`
  // on ANY error path. Without that callback, Stripe.js silently discards the
  // wallet authorization and never submits the PaymentIntent — the PI stays in
  // `requires_payment_method` and the user sees a generic failure.
  const handleExpressConfirm = async (event) => {
    if (!stripe || !elements) {
      event?.paymentFailed?.({ reason: "fail", message: t.stripeNotReady });
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    const returnUrl = `${window.location.origin}/success/${orderId}`;
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: returnUrl,
          // For Apple Pay / Google Pay, DO NOT override billing details — the
          // wallet provides the user's real billing info (name, email, country).
        },
      });
      if (error) {
        console.error("Express confirmPayment error:", error);
        const msg = error.message || error.code || t.expressFail;
        event?.paymentFailed?.({ reason: "fail", message: msg });
        setErrorMsg(msg);
        setSubmitting(false);
        return;
      }
      if (paymentIntent?.status === "succeeded") {
        toast.success(t.paySuccess);
        onSuccess();
        return;
      }
      if (paymentIntent && (paymentIntent.status === "processing" || paymentIntent.status === "requires_action")) {
        // Valid in-flight states — Stripe will redirect via return_url or finish on its own.
        return;
      }
      // Reached here with no success and no obvious failure — signal failure
      // so the wallet sheet closes cleanly and the user can retry.
      event?.paymentFailed?.({ reason: "fail", message: "Payment could not be completed." });
      setSubmitting(false);
    } catch (err) {
      console.error("Express confirmPayment threw:", err);
      const detail = err?.message || err?.error?.message || "";
      const msg = detail || t.expressRetry;
      event?.paymentFailed?.({ reason: "fail", message: msg });
      setErrorMsg(detail ? `${t.confirmFailPrefix}: ${detail}` : t.expressRetry);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="stripe-form" dir={dir}>
      {/* Express Checkout (Apple Pay / Google Pay) */}
      <div
        className={walletReady ? "block" : "hidden"}
        data-testid="express-checkout-wrap"
      >
        <ExpressCheckoutElement
          onReady={({ availablePaymentMethods }) => {
            if (availablePaymentMethods && Object.values(availablePaymentMethods).some(Boolean)) {
              setWalletReady(true);
            }
          }}
          onConfirm={handleExpressConfirm}
          options={{
            buttonHeight: 50,
            buttonTheme: { applePay: "black", googlePay: "black" },
            buttonType: { applePay: "buy", googlePay: "buy" },
            paymentMethods: {
              applePay: "always",
              googlePay: "always",
              link: "never",
              paypal: "never",
              amazonPay: "never",
            },
          }}
        />
        <div className={`flex items-center gap-3 my-4 text-[11px] ${themeAI ? "text-[#6B7280]" : "text-[#A1B0C6]"}`}>
          <span className={`flex-1 h-px ${themePink ? "bg-[#E8DDD8]" : themeAI ? "bg-[#E5E7EB]" : "bg-[#C5A059]/20"}`} />
          <span className={themePink ? "text-[#8D6E79]" : themeAI ? "text-[#6B7280]" : ""}>{t.orPayCard}</span>
          <span className={`flex-1 h-px ${themePink ? "bg-[#E8DDD8]" : themeAI ? "bg-[#E5E7EB]" : "bg-[#C5A059]/20"}`} />
        </div>
      </div>

      <div className={`bg-white rounded-2xl p-4 border ${themePink ? "border-[#E8DDD8]" : "border-[#C5A059]/30 rounded-sm"}`} style={{ direction: "ltr" }}>
        <PaymentElement
          options={{
            layout: { type: "tabs", defaultCollapsed: false },
            // Hide ALL billing fields for a minimal-friction checkout.
            fields: { billingDetails: "never" },
            // Suppress wallet duplication — ExpressCheckoutElement above
            // already shows Apple Pay / Google Pay buttons.
            wallets: { applePay: "never", googlePay: "never", link: "never" },
            // Force card as the only visible PM. Stripe will hide all others
            // (Amazon Pay, Klarna, etc.) that we don't explicitly request.
            paymentMethodOrder: ["card"],
          }}
        />
      </div>

      {errorMsg && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-sm p-3" data-testid="stripe-error">
          {errorMsg}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="stripe-submit-btn"
        className={
          themePink
            ? "w-full bg-[#8D6E79] text-white hover:bg-[#7a5d68] font-black text-lg py-7 rounded-full shadow-[0_15px_40px_rgba(141,110,121,0.35)]"
            : themeAI
              ? "w-full bg-[#0F172A] text-white hover:bg-[#1E293B] font-black text-lg py-7 rounded-md shadow-[0_12px_32px_rgba(15,23,42,0.35)]"
              : "w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-black text-lg py-7 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.4)]"
        }
      >
        {submitting ? (
          <span className="flex items-center gap-2 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> {t.processing}
          </span>
        ) : (
          <span className="flex flex-col items-center gap-0.5 leading-tight">
            <span>{t.payNow} &nbsp;·&nbsp; {price.primary}</span>
            {price.secondary && (
              <span className={`text-xs font-semibold ${themePink || themeAI ? "text-white/80" : "text-[#040914]/75"}`}>{price.secondary} USD</span>
            )}
          </span>
        )}
      </Button>

      <div className={`flex items-center justify-center gap-2 text-[10px] ${themePink ? "text-[#8D6E79]" : themeAI ? "text-[#6B7280]" : "text-[#A1B0C6]"}`}>
        <Lock className={`w-3 h-3 ${themePink ? "text-[#C8A977]" : themeAI ? "text-[#0F766E]" : "text-[#C5A059]"}`} /> {t.secureNote}
      </div>
    </form>
  );
}

export default function StripeCheckoutPanel({ orderId, amount, pkg, onSuccess, displayPriceOverride, themePink, themeAI }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);
  const tOuter = themeAI ? T.en : T.ar;
  // useRef guard prevents React StrictMode (or any double mount) from firing
  // two parallel /stripe-intent POSTs for the same order. Backend is also
  // idempotent (reuses existing PI), but stopping the second request reduces
  // log noise and a tiny race window where Stripe may briefly show a duplicate.
  const intentRequested = useRef(false);

  useEffect(() => {
    if (intentRequested.current) return;
    intentRequested.current = true;
    api
      .post(`/orders/${orderId}/stripe-intent`)
      .then((r) => setClientSecret(r.data.client_secret))
      .catch(() => setError(tOuter.initError));
  }, [orderId, tOuter.initError]);

  const options = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            appearance: {
              theme: "stripe",
              variables: themePink
                ? {
                    colorPrimary: "#8D6E79",
                    colorBackground: "#ffffff",
                    colorText: "#3E2F32",
                    colorDanger: "#dc2626",
                    fontFamily: "'IBM Plex Sans Arabic', Tajawal, system-ui, sans-serif",
                    spacingUnit: "4px",
                    borderRadius: "12px",
                  }
                : themeAI
                ? {
                    colorPrimary: "#0F172A",
                    colorBackground: "#ffffff",
                    colorText: "#0F172A",
                    colorDanger: "#dc2626",
                    fontFamily: "'Inter','SF Pro Display',system-ui,-apple-system,sans-serif",
                    spacingUnit: "4px",
                    borderRadius: "8px",
                  }
                : {
                    colorPrimary: "#C5A059",
                    colorBackground: "#ffffff",
                    colorText: "#040914",
                    colorDanger: "#dc2626",
                    fontFamily: "Tajawal, system-ui, sans-serif",
                    spacingUnit: "4px",
                    borderRadius: "2px",
                  },
            },
            locale: themeAI ? "en" : "ar",
          }
        : null,
    [clientSecret, themePink, themeAI]
  );

  if (error) {
    return (
      <div className={`text-sm ${themePink ? "text-red-600 bg-red-50 border-red-200" : themeAI ? "text-red-600 bg-red-50 border-red-200" : "text-red-400 bg-red-500/10 border-red-500/30"} border rounded-sm p-4`} data-testid="stripe-init-error">
        {error}
      </div>
    );
  }

  if (!clientSecret || !STRIPE_PK) {
    return (
      <div className={`flex items-center gap-2 text-sm py-8 justify-center ${themePink ? "text-[#8D6E79]" : themeAI ? "text-[#6B7280]" : "text-[#A1B0C6]"}`} data-testid="stripe-loading" dir={themeAI ? "ltr" : "rtl"}>
        <Loader2 className="w-4 h-4 animate-spin" /> {tOuter.loading}
      </div>
    );
  }

  return (
    <Elements stripe={getStripe()} options={options}>
      <InnerForm
        orderId={orderId}
        amount={amount}
        pkg={pkg}
        onSuccess={onSuccess}
        displayPriceOverride={displayPriceOverride}
        themePink={themePink}
        themeAI={themeAI}
      />
    </Elements>
  );
}
